import jsPDF from "jspdf";
import logoBase64 from "../assets/logoBase64";
import autoTable from "jspdf-autotable";
import { ResultService } from "../services/resultService";
import React, { useState } from "react";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Section } from "../types/section/section";
import { Concorrente } from "../types/concorrenti/concorrente";

interface ClassificaTabProps {
  sections: Section[];
  concorrenti: Concorrente[];
  competitionName: string;
  competitionDate?: string;
}

interface ClassificaEntry {
  concorrente: Concorrente;
  position: number;
  trote: number;
  punteggio: number;
}

type ClassifichePerSettore = {
  [sectionId: number]: ClassificaEntry[];
};

const ClassificaTab: React.FC<ClassificaTabProps> = ({
  sections,
  concorrenti,
  competitionName,
  competitionDate,
}) => {
  const [saving, setSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState<string | null>(null);
  const [selectedSectionId, setSelectedSectionId] = useState<number | null>(
    null
  );
  const [classifiche, setClassifiche] = useState<ClassifichePerSettore>({});
  const [availableConcorrenti, setAvailableConcorrenti] =
    useState<Concorrente[]>(concorrenti);

  React.useEffect(() => {
    const fetchRanking = async () => {
      if (!sections || sections.length === 0) return;
      const competitionId = sections[0]?.competitionId;
      if (!competitionId) return;
      try {
        const results = await ResultService.getRanking(competitionId);
        const classificheFromApi: ClassifichePerSettore = {};
        results.forEach((item) => {
          const concorrente = concorrenti.find(
            (c) => c.id === item.competitorId
          );
          if (!concorrente) return;
          if (!classificheFromApi[item.sectionId])
            classificheFromApi[item.sectionId] = [];
          classificheFromApi[item.sectionId].push({
            concorrente,
            position: item.position,
            trote: item.totalFishCount ?? 0,
            punteggio: item.score ?? 0,
          });
        });
        Object.keys(classificheFromApi).forEach((sectionId) => {
          classificheFromApi[Number(sectionId)] = classificheFromApi[
            Number(sectionId)
          ].sort((a, b) => a.position - b.position);
        });
        setClassifiche(classificheFromApi);
      } catch (err) {
        console.error("Errore nel recupero della classifica:", err);
      }
    };
    fetchRanking();
  }, [sections, concorrenti]);

  const handleRemoveFromClassifica = (
    sectionId: number,
    concorrenteId: number
  ) => {
    setClassifiche((prev) => {
      const prevClassifica = prev[sectionId] || [];
      const newClassifica = prevClassifica
        .filter((e) => e.concorrente.id !== concorrenteId)
        .map((e, idx) => ({ ...e, position: idx + 1 }));
      // Salva la classifica aggiornata tramite API
      const competitionId = sections[0]?.competitionId || 0;
      ResultService.saveRanking({
        competitionId,
        sectionId,
        ranking: newClassifica.map((entry) => ({
          concorrenteId: entry.concorrente.id,
          position: entry.position,
          catches: entry.trote,
          score: entry.punteggio,
        })),
      }).catch((err) => {
        setSaveMessage("Errore nel salvataggio della classifica dopo la rimozione.");
        console.error("Errore salvataggio classifica:", err);
      });
      return {
        ...prev,
        [sectionId]: newClassifica,
      };
    });
    if (selectedSectionId === sectionId) {
      const concorrente = classifiche[sectionId]?.find(
        (e) => e.concorrente.id === concorrenteId
      )?.concorrente;
      if (concorrente) {
        setAvailableConcorrenti((prev) => [...prev, concorrente]);
      }
    }
  };

  const handleSaveAllRankings = async () => {
    setSaving(true);
    setSaveMessage(null);
    const competitionId = sections[0]?.competitionId || 0;
    const savePromises = sections.map(async (section) => {
      const classifica = classifiche[section.id] || [];
      if (classifica.length === 0) return null;
      try {
        await ResultService.saveRanking({
          competitionId,
          sectionId: section.id,
          ranking: classifica.map((entry) => ({
            concorrenteId: entry.concorrente.id,
            position: entry.position,
            catches: entry.trote,
            score: entry.punteggio,
          })),
        });
        return null;
      } catch (err) {
        console.error(err);
        return `Errore nel salvataggio della classifica per settore ${section.name}. `;
      }
    });
    const results = await Promise.all(savePromises);
    const errorMsg = results.filter(Boolean).join("");
    if (!errorMsg) {
      setSaveMessage("Tutte le classifiche sono state salvate con successo!");
    } else {
      setSaveMessage(errorMsg || "Errore nel salvataggio di una o più classifiche");
    }
    setSaving(false);
  };

  const sensors = useSensors(useSensor(PointerSensor));

  const handleSectionChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const sectionId = Number(e.target.value);
    setSelectedSectionId(sectionId);
    // Trova tutti i concorrenti già assegnati a qualsiasi settore
    const giaAssegnati = Object.values(classifiche)
      .flat()
      .map((entry) => entry.concorrente.id);
    setAvailableConcorrenti(
      concorrenti.filter((c) => !giaAssegnati.includes(c.id))
    );
  };

  const handleAddToClassifica = (concorrenteId: number) => {
    if (!selectedSectionId) return;
    const concorrente = availableConcorrenti.find(
      (c) => c.id === concorrenteId
    );
    if (concorrente) {
      setClassifiche((prev) => {
        const prevClassifica = prev[selectedSectionId] || [];
        return {
          ...prev,
          [selectedSectionId]: [
            ...prevClassifica,
            {
              concorrente,
              position: prevClassifica.length + 1,
              trote: 0,
              punteggio: 0,
            },
          ],
        };
      });
      setAvailableConcorrenti((prev) =>
        prev.filter((c) => c.id !== concorrenteId)
      );
    }
  };

  const handleClassificaFieldChange = (
    sectionId: number,
    concorrenteId: number,
    field: "trote" | "punteggio",
    value: number
  ) => {
    setClassifiche((prev) => {
      const prevClassifica = prev[sectionId] || [];
      return {
        ...prev,
        [sectionId]: prevClassifica.map((entry) =>
          entry.concorrente.id === concorrenteId
            ? { ...entry, [field]: value }
            : entry
        ),
      };
    });
  };

  const handleExportPDF = () => {
    const doc = new jsPDF();
  // Aggiungi il logo in alto a sinistra
  doc.addImage(logoBase64, 'PNG', 10, 8, 24, 32); // x, y, width, height
    const garaName = competitionName || "Gara";
    const garaDate = competitionDate ? new Date(competitionDate) : null;
    const formattedDate = garaDate
      ? `${garaDate.getDate().toString().padStart(2, "0")}-${(
          garaDate.getMonth() + 1
        )
          .toString()
          .padStart(2, "0")}-${garaDate.getFullYear()}`
      : "";
    doc.setFontSize(16);
    // Allinea il titolo verticalmente al centro del logo e spostalo più a destra
    doc.text(
      `${garaName}${formattedDate ? " (" + formattedDate + ")" : ""}`,
      44,
      24
    );
    // Aggiungi più margine tra intestazione e tabelle
    let y = 48;
    sections.forEach((section) => {
      doc.setFontSize(13);
      doc.text(section.name, 10, y);
      y += 6;
      const rows = (classifiche[section.id] || []).map((entry) => [
        `${entry.concorrente.firstName} ${entry.concorrente.lastName}`,
        entry.punteggio,
        entry.trote,
        entry.position,
      ]);
      // Calcolo totale trote
      const totaleTrote = (classifiche[section.id] || []).reduce(
        (sum, entry) => sum + entry.trote,
        0
      );
      // Riga finale con totale trote
      const footerRow = [
        "Totale trote settore",
        "",
        totaleTrote,
        "",
      ];
      autoTable(doc, {
        startY: y,
        head: [["Nome e Cognome", "Punti", "Trote", "Posizione"]],
        body: rows,
        foot: [footerRow],
        theme: "grid",
        styles: { fontSize: 10 },
        headStyles: { fillColor: [41, 128, 185], textColor: 255 },
        margin: { left: 10, right: 10 },
      });
      y = (doc as any).lastAutoTable?.finalY
        ? (doc as any).lastAutoTable.finalY + 10
        : y + 30;
    });
    doc.save(`${competitionName}.pdf`);
  };

  return (
    <div>
      <div className="mb-4">
        <label className="block mb-2 font-medium">Seleziona settore</label>
        <select
          className="w-full border border-gray-300 rounded-md px-3 py-2"
          value={selectedSectionId ?? ""}
          onChange={handleSectionChange}
        >
          <option value="">Settore</option>
          {sections.map((section) => (
            <option key={section.id} value={section.id}>
              {section.name}
            </option>
          ))}
        </select>
      </div>
      {sections.map((section) => (
        <div key={section.id} className="mb-8">
          {selectedSectionId === section.id && (
            <div className="mb-4">
              <label className="block mb-2 font-medium">
                Aggiungi concorrente in classifica
              </label>
              <select
                className="w-full border border-gray-300 rounded-md px-3 py-2"
                value=""
                onChange={(e) => handleAddToClassifica(Number(e.target.value))}
                disabled={availableConcorrenti.length === 0}
              >
                <option value="">Concorrente</option>
                {availableConcorrenti.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.firstName} {c.lastName}
                  </option>
                ))}
              </select>
            </div>
          )}
          <h4 className="font-semibold mb-2">Classifica ({section.name})</h4>
          {/* Etichette sopra la classifica */}
          <div className="hidden sm:flex flex-row flex-wrap items-center gap-2 w-full sm:w-auto font-semibold text-gray-700 mb-2 px-1 text-xs sm:text-base overflow-x-auto">
            <span className="w-6 flex-shrink-0"></span> {/* posizione */}
            <span className="flex-1 min-w-0">Concorrente</span>
            <span className="w-20 text-center">Trote</span>
            <span className="w-24 text-center">Punti</span>
            <span className="w-8"></span> {/* bottone rimozione */}
          </div>

          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={(event: DragEndEvent) => {
              const { active, over } = event;
              if (active.id !== over?.id) {
                setClassifiche((prev) => {
                  const prevClassifica = prev[section.id] || [];
                  const oldIndex = prevClassifica.findIndex(
                    (e) => e.concorrente.id === active.id
                  );
                  const newIndex = prevClassifica.findIndex(
                    (e) => e.concorrente.id === over?.id
                  );
                  const newArr = arrayMove(
                    prevClassifica,
                    oldIndex,
                    newIndex
                  ).map((entry: ClassificaEntry, idx: number) => ({
                    ...entry,
                    position: idx + 1,
                  }));
                  return {
                    ...prev,
                    [section.id]: newArr,
                  };
                });
              }
            }}
          >
            <SortableContext
              items={(classifiche[section.id] || []).map(
                (entry) => entry.concorrente.id
              )}
              strategy={verticalListSortingStrategy}
            >
              <ul className="space-y-2">
                {(classifiche[section.id] || []).map((entry) => (
                  <SortableClassificaItem
                    key={entry.concorrente.id}
                    id={entry.concorrente.id}
                    entry={entry}
                    sectionId={section.id}
                    onFieldChange={handleClassificaFieldChange}
                    onRemove={handleRemoveFromClassifica}
                  />
                ))}
              </ul>
            </SortableContext>
          </DndContext>
        </div>
      ))}

      <div className="flex gap-2 mb-2">
        <button
          className="bg-brand-700 hover:bg-brand-600 text-white font-semibold py-2 px-4 rounded-lg"
          onClick={handleSaveAllRankings}
          disabled={
            saving ||
            Object.values(classifiche).every((arr) => !arr || arr.length === 0)
          }
        >
          {saving ? "Salvataggio..." : "Salva tutte le classifiche"}
        </button>
        <button
          className="bg-brand-700 hover:bg-brand-600 text-white font-semibold py-2 px-4 rounded-lg"
          onClick={handleExportPDF}
          disabled={Object.values(classifiche).every(
            (arr) => !arr || arr.length === 0
          )}
        >
          Esporta PDF
        </button>
      </div>
      {saveMessage && (
        <div
          className={`mt-2 text-sm ${
            saveMessage.includes("successo") ? "text-green-600" : "text-red-600"
          }`}
        >
          {saveMessage}
        </div>
      )}
    </div>
  );
};

// Componente per ogni riga drag & drop
interface SortableClassificaItemProps {
  id: number;
  entry: ClassificaEntry;
  sectionId: number;
  onFieldChange: (
    sectionId: number,
    concorrenteId: number,
    field: "trote" | "punteggio",
    value: number
  ) => void;
  onRemove: (sectionId: number, concorrenteId: number) => void;
}

function SortableClassificaItem({ id, entry, sectionId, onFieldChange, onRemove }: SortableClassificaItemProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
    background: isDragging ? "#f3f4f6" : undefined,
  };
  return (
    <li ref={setNodeRef} style={style} className="px-1 py-1 text-xs sm:text-base">
      {/* Mobile: struttura su più righe */}
      <div className="flex flex-col gap-1 sm:hidden">
        <div className="flex flex-row items-center w-full gap-2">
          <div className="flex flex-row items-center flex-1 gap-2 cursor-move" {...attributes} {...listeners}>
            <span className="font-bold w-6 flex-shrink-0">{entry.position}</span>
            <span className="flex-1 min-w-0 truncate">
              {entry.concorrente.firstName} {entry.concorrente.lastName}
            </span>
          </div>
          <button
            type="button"
            className="ml-2 text-red-600 hover:text-red-800"
            title="Rimuovi concorrente dalla classifica"
            onClick={() => onRemove(sectionId, entry.concorrente.id)}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="20"
              height="20"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>
        <div className="flex flex-row items-center gap-2 w-full">
          <label className="w-14 text-gray-600">trote</label>
          <input
            type="number"
            min={0}
            className="w-16 border border-gray-300 rounded px-1 py-1 text-center"
            value={entry.trote}
            onChange={e =>
              onFieldChange(
                sectionId,
                entry.concorrente.id,
                "trote",
                Number(e.target.value)
              )
            }
            placeholder="Trote"
            title="Trote pescate"
          />
        </div>
        <div className="flex flex-row items-center gap-2 w-full">
          <label className="w-14 text-gray-600">punti</label>
          <input
            type="number"
            min={0}
            className="w-16 border border-gray-300 rounded px-1 py-1 text-center"
            value={entry.punteggio}
            onChange={e =>
              onFieldChange(
                sectionId,
                entry.concorrente.id,
                "punteggio",
                Number(e.target.value)
              )
            }
            placeholder="Punteggio"
            title="Punteggio"
          />
        </div>
      </div>
      {/* Desktop: struttura compatta su una riga */}
      <div className="hidden sm:flex flex-row items-center gap-2 w-full">
        <div className="flex flex-row items-center flex-1 gap-2 cursor-move" {...attributes} {...listeners}>
          <span className="font-bold w-6 flex-shrink-0">{entry.position}</span>
          <span className="flex-1 min-w-0 truncate">
            {entry.concorrente.firstName} {entry.concorrente.lastName}
          </span>
        </div>
        <input
          type="number"
          min={0}
          className="w-20 border border-gray-300 rounded px-2 py-1 text-center"
          value={entry.trote}
          onChange={e =>
            onFieldChange(
              sectionId,
              entry.concorrente.id,
              "trote",
              Number(e.target.value)
            )
          }
          placeholder="Trote"
          title="Trote pescate"
        />
        <input
          type="number"
          min={0}
          className="w-24 border border-gray-300 rounded px-2 py-1 text-center"
          value={entry.punteggio}
          onChange={e =>
            onFieldChange(
              sectionId,
              entry.concorrente.id,
              "punteggio",
              Number(e.target.value)
            )
          }
          placeholder="Punteggio"
          title="Punteggio"
        />
        <button
          type="button"
          className="ml-2 text-red-600 hover:text-red-800"
          title="Rimuovi concorrente dalla classifica"
          onClick={() => onRemove(sectionId, entry.concorrente.id)}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="20"
            height="20"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>
      </div>
    </li>
  );
}

export default ClassificaTab;
